import { DatePicker, Select, Card, Typography, Space, Form, InputNumber, Checkbox, Button, Radio, Flex } from "antd"
import TimePicker from "../../common/TimePicker"
import dayjs from "dayjs"
import DurationPicker from "../../common/DurationPicker"
import { EventFormState } from "./useEventFormState"
import { repeatPlaceFromDate } from "./recurringScheduleModel"
import { blue } from '@ant-design/colors'

const { Text } = Typography

type Props = {
  formState: EventFormState
  hideRepeatSelector?: boolean
}

const EventFormScheduleFields = ({
  formState,
  hideRepeatSelector = false,
}: Props) => {
  const {
    form,
    formValues,
    setFormValues,
    assignedWorkareas,
    calculatedEndDate,
  } = formState

  const {
    startDate,
    startTime,
    estimatedDuration,
    endDate,
    isAllDay,
    repeatUnit,
    repeatEndType,
    repeatOccurrences,
    repeatEndsOn,
    endDateMode,
    manhoursPerDay,
    totalEstimatedHours,
    workDays,
  } = formValues

  const showRepeatSelector = !isAllDay && !hideRepeatSelector
  const showRecurringSettings = showRepeatSelector && repeatUnit !== 'none'

  return (
    <div style={{ marginBottom: "24px" }}>
      <Text style={{ fontSize: "14px", display: "block", marginBottom: "8px" }}>
        Schedule
      </Text>
      <Card style={{ padding: "16px" }}>
        <div style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>
              {isAllDay ? "Start Date" : "Date"}
            </div>
            <DatePicker
              value={startDate}
              onChange={(date) => {
                if (date) {
                  const updates: Partial<typeof formValues> = {
                    startDate: date,
                    repeatPlace: repeatPlaceFromDate(date),
                  }
                  if (!repeatEndsOn || repeatEndsOn.isBefore(date, "day")) {
                    updates.repeatEndsOn = date
                  }
                  if (isAllDay && endDate.isBefore(date, "day")) {
                    updates.endDate = date
                  }
                  setFormValues(updates)
                  form.setFieldsValue({ repeatPlace: updates.repeatPlace })
                }
              }}
              style={{ width: "100%" }}
              format="MMM D, YYYY"
              allowClear={false}
              disabledDate={(current) => {
                if (repeatUnit !== "none" && repeatEndsOn) {
                  return current && current.isAfter(repeatEndsOn, "day")
                }
                if (isAllDay && endDateMode === "manual" && endDate) {
                  return current && current.isAfter(endDate, "day")
                }
                return false
              }}
            />
          </div>
        </div>

        <div style={{ marginBottom: "16px" }}>
          <Checkbox
            checked={isAllDay}
            onChange={(e) => formState.handleAllDayChange(e.target.checked)}
          >
            This is an all-day or multi-day event
          </Checkbox>
        </div>

        {isAllDay && (
          <div style={{ marginBottom: "16px" }}>
            <div style={{ fontSize: "12px", color: "#666", marginBottom: "8px" }}>
              End Date
            </div>
            <Radio.Group
              value={endDateMode}
              onChange={(e) => setFormValues({ endDateMode: e.target.value })}
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              <Radio value="sameDay">The Same Day</Radio>
              <div>
                <Radio value="manual">On a Specific Date</Radio>
                {endDateMode === "manual" && (
                  <DatePicker
                    value={endDate}
                    onChange={(date) => {
                      if (date) {
                        setFormValues({ endDate: date })
                      }
                    }}
                    style={{ width: "200px", marginTop: "8px", marginLeft: "24px" }}
                    format="MMM D, YYYY"
                    allowClear={false}
                    disabledDate={(current) =>
                      current && current.isBefore(startDate, "day")
                    }
                  />
                )}
              </div>
              <div>
                <Radio value="calculated">Based on Man Hours</Radio>
                {endDateMode === "calculated" && <div style={{ marginLeft: "24px", marginTop: "8px" }}>
                  <div style={{ marginBottom: assignedWorkareas.length > 0 ? "4px" : "8px" }}>
                    <span style={{ fontSize: "14px", color: "#666", marginRight: "8px" }}>Total Manhours</span>
                    <InputNumber
                      min={0}
                      disabled={endDateMode !== "calculated"}
                      value={totalEstimatedHours}
                      onChange={(value) => {
                        if (value != null) {
                          setFormValues({ totalEstimatedHours: value })
                        }
                      }}
                      style={{ width: "90px" }}
                    />
                  </div>
                  {assignedWorkareas.length > 0 && (
                    <div style={{ fontSize: "12px", marginBottom: "8px" }}>
                      <span style={{ color: "#999" }}>Selected work areas total&nbsp;</span>
                      <a
                        onClick={() =>
                          setFormValues({
                            totalEstimatedHours: assignedWorkareas.reduce((sum, wa) => sum + wa.estimatedHours, 0),
                          })
                        }
                        style={{ fontSize: "12px", color: blue.primary }}
                      >
                        {assignedWorkareas.reduce((sum, wa) => sum + wa.estimatedHours, 0)} manhours
                      </a>
                    </div>
                  )}
                  <div style={{ marginBottom: "8px" }}>
                    <span style={{ fontSize: "14px", color: "#666", marginRight: "8px" }}>Manhours per Day</span>
                    <InputNumber
                      min={1}
                      disabled={endDateMode !== "calculated"}
                      value={manhoursPerDay}
                      onChange={(value) => {
                        if (value) {
                          setFormValues({ manhoursPerDay: value })
                        }
                      }}
                      style={{ width: "70px" }}
                    />
                  </div>
                  <div style={{ marginBottom: "8px" }}>
                    <span style={{ fontSize: "14px", color: "#666", display: "block", marginBottom: "4px" }}>Work Days</span>
                    <Flex gap="4px" wrap="wrap">
                      {[
                        { value: 0, label: "Sun" },
                        { value: 1, label: "Mon" },
                        { value: 2, label: "Tue" },
                        { value: 3, label: "Wed" },
                        { value: 4, label: "Thu" },
                        { value: 5, label: "Fri" },
                        { value: 6, label: "Sat" },
                      ].map((day) => {
                        const isSelected = workDays.includes(day.value)
                        return (
                          <Button
                            key={day.value}
                            size="small"
                            type={isSelected ? "primary" : "default"}
                            disabled={endDateMode !== "calculated"}
                            onClick={() => {
                              const updated = isSelected
                                ? workDays.filter((d) => d !== day.value)
                                : [...workDays, day.value]
                              setFormValues({ workDays: updated })
                            }}
                            style={{ width: "36px", padding: 0 }}
                          >
                            {day.label}
                          </Button>
                        )
                      })}
                    </Flex>
                  </div>
                  <div style={{ fontSize: "14px", color: "#666" }}>
                    End Date:&nbsp;
                    <Text strong style={{ fontSize: 14 }}>
                      {(calculatedEndDate ?? startDate).format("MMM D, YYYY")}
                    </Text>
                  </div>
                </div>}
              </div>
            </Radio.Group>
          </div>
        )}

        {!isAllDay && (
          <div style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>
                Start Time
              </div>
              <TimePicker
                value={startTime}
                onChange={(time) => setFormValues({ startTime: time })}
                minuteStep={5}
              />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>
                Estimated Duration
              </div>
              <DurationPicker
                value={estimatedDuration}
                onChange={(duration) => setFormValues({ estimatedDuration: duration })}
                increment={300}
                style={{ width: "100%" }}
              />
              <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                Ends at {startTime
                  .add(estimatedDuration.seconds, "second")
                  .format("h:mm A")}
              </div>
            </div>
          </div>
        )}

        {showRepeatSelector && (
          <>
            <div style={{ marginBottom: showRecurringSettings ? 16 : 0 }}>
              <div style={{ fontSize: "12px", color: "#666", marginBottom: "8px" }}>
                Repeats
              </div>
              <Form.Item noStyle name="repeatUnit" initialValue="none">
                <Select
                  style={{ width: "100%" }}
                  allowClear={false}
                  onChange={(value) => setFormValues({ repeatUnit: value })}
                >
                  <Select.Option value="none">Does not repeat</Select.Option>
                  <Select.Option value="day">Daily</Select.Option>
                  <Select.Option value="week">
                    Weekly on {startDate.format("dddd")}s
                  </Select.Option>
                  <Select.Option value="month">Monthly</Select.Option>
                  <Select.Option value="year">Annually</Select.Option>
                </Select>
              </Form.Item>
            </div>
          </>
        )}

        {showRecurringSettings && (
          <>
            <div style={{ marginBottom: 16 }}>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.repeatUnit !== currentValues.repeatUnit
                }
              >
                {({ getFieldValue }) => {
                  const repeatUnitValue = getFieldValue("repeatUnit")
                  const dayOfWeek = startDate.format("dddd")

                  switch (repeatUnitValue) {
                    case "day":
                      return (
                        <Space>
                          Every{" "}
                          <Form.Item
                            noStyle
                            name="repeatCount"
                            initialValue={1}
                            rules={[
                              {
                                required: true,
                                message: "Please enter repeat count",
                              },
                            ]}
                          >
                            <InputNumber
                              min={1}
                              style={{ width: 60 }}
                              onChange={(value) => setFormValues({ repeatCount: value || 1 })}
                            />
                          </Form.Item>{" "}
                          days
                        </Space>
                      )
                    case "week":
                      return (
                        <Space>
                          Every{" "}
                          <Form.Item
                            noStyle
                            name="repeatCount"
                            initialValue={1}
                            rules={[
                              {
                                required: true,
                                message: "Please enter repeat count",
                              },
                            ]}
                          >
                            <InputNumber
                              min={1}
                              style={{ width: 60 }}
                              onChange={(value) => setFormValues({ repeatCount: value || 1 })}
                            />
                          </Form.Item>{" "}
                          weeks
                        </Space>
                      )
                    case "month":
                      return (
                        <Space direction="vertical" size={8}>
                          <Space>
                            Every{" "}
                            <Form.Item
                              noStyle
                              name="repeatCount"
                              initialValue={1}
                              rules={[
                                {
                                  required: true,
                                  message: "Please enter repeat count",
                                },
                              ]}
                            >
                              <InputNumber
                                min={1}
                                style={{ width: 60 }}
                                onChange={(value) => setFormValues({ repeatCount: value || 1 })}
                              />
                            </Form.Item>{" "}
                            months
                          </Space>
                          <Space>
                            On the{" "}
                            <Form.Item
                              noStyle
                              name="repeatPlace"
                              initialValue={["1st"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Please select repeat place",
                                },
                              ]}
                            >
                              <Select
                                style={{ width: 120 }}
                                mode="multiple"
                                placeholder="Select"
                                maxTagCount={3}
                                maxTagTextLength={10}
                                allowClear={false}
                                onChange={(value) => setFormValues({ repeatPlace: value })}
                              >
                                <Select.Option value="1st">1st</Select.Option>
                                <Select.Option value="2nd">2nd</Select.Option>
                                <Select.Option value="3rd">3rd</Select.Option>
                                <Select.Option value="4th">4th</Select.Option>
                                <Select.Option value="last">Last</Select.Option>
                              </Select>
                            </Form.Item>{" "}
                            {dayOfWeek} of the month
                          </Space>
                        </Space>
                      )
                    case "year":
                      return (
                        <Space direction="vertical" size={8}>
                          <Space>
                            Every{" "}
                            <Form.Item
                              noStyle
                              name="repeatCount"
                              initialValue={1}
                              rules={[
                                {
                                  required: true,
                                  message: "Please enter repeat count",
                                },
                              ]}
                            >
                              <InputNumber
                                min={1}
                                style={{ width: 60 }}
                                onChange={(value) => setFormValues({ repeatCount: value || 1 })}
                              />
                            </Form.Item>{" "}
                            years
                          </Space>
                          <Space>
                            On the{" "}
                            <Form.Item
                              noStyle
                              name="repeatPlace"
                              initialValue={["1st"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Please select repeat place",
                                },
                              ]}
                            >
                              <Select
                                style={{ width: 120 }}
                                mode="multiple"
                                placeholder="Select"
                                maxTagCount={3}
                                maxTagTextLength={10}
                                allowClear={false}
                                onChange={(value) => setFormValues({ repeatPlace: value })}
                              >
                                <Select.Option value="1st">1st</Select.Option>
                                <Select.Option value="2nd">2nd</Select.Option>
                                <Select.Option value="3rd">3rd</Select.Option>
                                <Select.Option value="4th">4th</Select.Option>
                                <Select.Option value="last">Last</Select.Option>
                              </Select>
                            </Form.Item>{" "}
                            {dayOfWeek} of{" "}
                            <Form.Item
                              noStyle
                              name="repeatMonth"
                              initialValue={[dayjs().month() + 1]}
                              rules={[
                                {
                                  required: true,
                                  message: "Please select month",
                                },
                              ]}
                            >
                              <Select
                                style={{ width: 120 }}
                                mode="multiple"
                                placeholder="Select"
                                maxTagCount={3}
                                maxTagTextLength={10}
                                allowClear={false}
                                onChange={(value) => setFormValues({ repeatMonth: value })}
                              >
                                <Select.Option value={1}>January</Select.Option>
                                <Select.Option value={2}>February</Select.Option>
                                <Select.Option value={3}>March</Select.Option>
                                <Select.Option value={4}>April</Select.Option>
                                <Select.Option value={5}>May</Select.Option>
                                <Select.Option value={6}>June</Select.Option>
                                <Select.Option value={7}>July</Select.Option>
                                <Select.Option value={8}>August</Select.Option>
                                <Select.Option value={9}>September</Select.Option>
                                <Select.Option value={10}>October</Select.Option>
                                <Select.Option value={11}>November</Select.Option>
                                <Select.Option value={12}>December</Select.Option>
                              </Select>
                            </Form.Item>
                          </Space>
                        </Space>
                      )
                    default:
                      return null
                  }
                }}
              </Form.Item>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: "12px", color: "#666", marginBottom: "8px" }}>
                Ends
              </div>
              <Radio.Group
                value={repeatEndType}
                onChange={(e) => setFormValues({ repeatEndType: e.target.value })}
                style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Radio value="date">On</Radio>
                  <DatePicker
                    value={repeatEndsOn}
                    onChange={(date) => setFormValues({ repeatEndType: 'date', repeatEndsOn: date })}
                    style={{ flex: 1 }}
                    format="MMM D, YYYY"
                    allowClear={false}
                    placeholder="Select end date"
                    disabled={repeatEndType !== 'date'}
                    disabledDate={(current) =>
                      current && current.isBefore(startDate, "day")
                    }
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Radio value="occurrences">After</Radio>
                  <InputNumber
                    min={1}
                    max={365}
                    value={repeatOccurrences}
                    onChange={(value) => {
                      if (value) {
                        setFormValues({ repeatEndType: 'occurrences', repeatOccurrences: value })
                      }
                    }}
                    disabled={repeatEndType !== 'occurrences'}
                    style={{ width: 80 }}
                  />
                  <span style={{ fontSize: 14, color: repeatEndType !== 'occurrences' ? '#bbb' : undefined }}>
                    occurrences
                  </span>
                </div>
              </Radio.Group>
            </div>
          </>
        )}
      </Card>
    </div>
  )
}

export default EventFormScheduleFields
